import React, { useState, useRef, useEffect } from "react";
import { MessageSquareText, X, Send, Loader2, Sparkles, Plus, AlertTriangle, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

const SUGGESTIONS = [
  "How many fire doors are on this sheet?",
  "What does the door schedule say?",
  "List the room names and areas",
  "What's the drawing scale?",
];

const AskDrawingChatbot = ({ projectId, getImages, pageCount = 1, hasBlueprint, onPush }) => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]); // {role, text, citation, extracted, pushed}
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const imagesCache = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text || busy) return;
    setQ("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      if (!imagesCache.current) imagesCache.current = getImages ? await getImages() : [];
      const images = imagesCache.current;
      if (!images || !images.length) { toast.error("Open a drawing first"); setBusy(false); return; }
      const { data } = await api.post(`/projects/${projectId}/ai/ask-drawing`, { images, question: text });
      setMsgs((m) => [...m, { role: "ai", text: data.answer, citation: data.citation, extracted: data.extracted }]);
    } catch (err) {
      setMsgs((m) => [...m, { role: "ai", text: "Sorry — I couldn't read that from this drawing. Try a clearer page or rephrase.", error: true }]);
    } finally {
      setBusy(false);
    }
  };

  const push = async (idx, item) => {
    try {
      await onPush(item);
      setMsgs((m) => m.map((x, i) => (i === idx ? { ...x, pushed: true } : x)));
      toast.success("Added to your estimate");
    } catch { toast.error("Couldn't add to estimate"); }
  };

  if (!hasBlueprint) return null;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ask-drawing-launch-btn"
          className="fixed bottom-5 left-5 z-40 inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white pl-3 pr-4 py-2.5 shadow-lg shadow-blue-600/30 transition-colors"
        >
          <Sparkles className="h-4 w-4" /> <span className="text-sm font-semibold">Ask the drawing</span>
        </button>
      )}

      {open && (
        <div
          className="fixed z-50 bg-white border border-slate-200 shadow-2xl flex flex-col
                     inset-x-0 bottom-0 max-h-[78vh] rounded-t-2xl
                     md:inset-x-auto md:right-0 md:top-16 md:bottom-0 md:w-[380px] md:max-h-none md:rounded-none md:border-l"
          data-testid="ask-drawing-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200" style={{ backgroundColor: "#16305C" }}>
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="h-4 w-4 text-blue-300" />
              <span className="font-display font-semibold text-sm">Ask the drawing</span>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ask-drawing-close-btn" className="text-slate-300 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {msgs.length === 0 && (
              <div data-testid="ask-drawing-empty">
                <p className="text-sm text-slate-600">Ask me anything about {pageCount > 1 ? `this ${pageCount}-page drawing set` : "the drawing that's open"} — counts, schedules, room areas, the scale. I'll search {pageCount > 1 ? "every page" : "the sheet"}.</p>
                <div className="mt-3 space-y-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => ask(s)}
                      data-testid="ask-drawing-suggestion"
                      className="block w-full text-left text-sm rounded-lg border border-slate-200 px-3 py-2 text-slate-700 hover:border-blue-400 hover:bg-blue-50/40 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-blue-600 text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}
                     data-testid={m.role === "user" ? "ask-drawing-user-msg" : "ask-drawing-ai-msg"}>
                  <div>{m.text}</div>
                  {m.citation && (
                    <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-white/70 rounded px-1.5 py-0.5">
                      <MapPin className="h-3 w-3" /> {m.citation}
                    </div>
                  )}
                  {m.extracted && (
                    m.pushed ? (
                      <div className="mt-2 text-[11px] font-medium text-emerald-600">✓ Added to estimate</div>
                    ) : (
                      <button
                        onClick={() => push(i, m.extracted)}
                        data-testid="ask-drawing-push-estimate"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 hover:bg-emerald-100"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add {m.extracted.quantity ? `${m.extracted.quantity} ` : ""}{m.extracted.unit} to estimate
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}

            {busy && (
              <div className="flex justify-start" data-testid="ask-drawing-loading">
                <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-slate-500 inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> {pageCount > 1 ? `Reading ${Math.min(pageCount, 8)} pages…` : "Reading the drawing…"}
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Trust note + input */}
          <div className="border-t border-slate-200 p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-[11px] text-amber-700">
              <AlertTriangle className="h-3 w-3" /> AI can make mistakes — always check the drawing before you price.
            </div>
            <div className="flex gap-2">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
                placeholder="Ask about this drawing…"
                data-testid="ask-drawing-input"
                className="h-10"
                disabled={busy}
              />
              <Button onClick={() => ask()} disabled={busy || !q.trim()} data-testid="ask-drawing-send-btn" className="h-10 bg-blue-600 hover:bg-blue-700 shrink-0">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AskDrawingChatbot;
